import { useEffect, useState } from 'react';
import { LuCheck, LuChevronDown, LuCopy, LuFileText } from 'react-icons/lu';
import { EmptyState, Skeleton } from '../../ui/Feedback';
import { VerdictLabel } from '../../ui/Labels';
import { useStrings } from '../../tenant/TenantProvider';
import { formatMemory, formatRelative } from '../../lib/format';

function CopyButton({ text }) {
  const t = useStrings();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      className="btn btn--sm code-block__copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? <LuCheck className="btn__icon" aria-hidden="true" /> : <LuCopy className="btn__icon" aria-hidden="true" />}
      {copied ? t('common.copied') : t('common.copy')}
    </button>
  );
}

export function SubmissionHistory({ load, version }) {
  const t = useStrings();
  const [state, setState] = useState({ status: 'loading', submissions: [], error: null });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    load().then((response) => {
      if (cancelled) return;
      if (response.success) setState({ status: 'ready', submissions: response.data.submissions, error: null });
      else setState({ status: 'error', submissions: [], error: response.error });
    });
    return () => {
      cancelled = true;
    };
  }, [load, version]);

  if (state.status === 'loading') {
    return (
      <div className="history-skeleton">
        <Skeleton height={44} />
        <Skeleton height={44} />
        <Skeleton height={44} />
      </div>
    );
  }

  if (state.status === 'error') return <p className="console-error">{state.error}</p>;

  if (state.submissions.length === 0) {
    return <EmptyState icon={LuFileText} title={t('workspace.history.emptyTitle')} text={t('workspace.history.emptyText')} />;
  }

  const accepted = state.submissions.filter((item) => item.verdict === 'Accepted').length;

  return (
    <div className="history">
      <p className="history__summary muted">
        {t('workspace.history.count', { count: state.submissions.length })} · {t('workspace.history.accepted', { count: accepted })}
      </p>
      <ul className="history__list">
        {state.submissions.map((submission) => {
          const open = expanded === submission.id;
          const memory = formatMemory(submission.memoryUsed);
          return (
            <li key={submission.id} className="history__item">
              <button type="button" className="history__row" aria-expanded={open} onClick={() => setExpanded(open ? null : submission.id)}>
                <span className="history__verdict">
                  <VerdictLabel verdict={submission.verdict} pending={submission.status === 'PENDING'} />
                  <span className="subtle history__when">{formatRelative(submission.submittedAt)}</span>
                </span>
                <span className="history__facts muted">
                  <span>{t(`languages.${submission.language}`, { fallback: submission.language })}</span>
                  {submission.executionTime && <span className="tabular">{submission.executionTime}s</span>}
                  {memory && <span className="tabular">{memory} MB</span>}
                </span>
                <LuChevronDown className={`history__chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
              </button>
              {open && (
                <div className="history__detail">
                  {submission.verdict !== 'Accepted' && submission.failedTestCase && (
                    <p className="muted">{t('workspace.history.failedOn', { number: submission.failedTestCase })}</p>
                  )}
                  {submission.error && <pre className="console-output__value console-output__value--danger">{submission.error}</pre>}
                  <div className="code-block">
                    <pre>{submission.code}</pre>
                    {submission.code && <CopyButton text={submission.code} />}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
